import * as React from 'react';

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton, SkeletonSpan } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface EmptySkeletonProps extends React.ComponentProps<'div'> {
  media?: 'image' | 'icon';
  descriptionLines?: number;
  actions?: number;
  link?: boolean;
}

export function EmptySkeleton({
  media = 'image',
  descriptionLines = 2,
  actions = 1,
  link = false,
  className,
  ...props
}: EmptySkeletonProps) {
  const isIcon = media === 'icon';

  return (
    <Empty data-slot="empty-skeleton" aria-hidden className={className} {...props}>
      <EmptyHeader>
        {isIcon ? (
          <EmptyMedia
            variant="icon"
            className="animate-pulse border-transparent bg-muted bg-none p-0"
          />
        ) : (
          <EmptyMedia>
            <Skeleton className="size-34 rounded-2xl" />
          </EmptyMedia>
        )}
        <EmptyTitle className={cn('flex items-center justify-center', isIcon ? 'h-6' : 'h-7')}>
          <Skeleton className="h-4 w-40" />
        </EmptyTitle>
        <EmptyDescription className="flex flex-col items-center">
          {Array.from({ length: descriptionLines }, (_, index) => (
            <span key={index} className="flex h-5 items-center">
              <SkeletonSpan
                className={cn('h-3', index === descriptionLines - 1 ? 'w-56' : 'w-88')}
              />
            </span>
          ))}
        </EmptyDescription>
      </EmptyHeader>
      {(actions > 0 || link) && (
        <EmptyContent>
          {actions > 0 && (
            <div className="flex items-center gap-3">
              {Array.from({ length: actions }, (_, index) => (
                <Skeleton key={index} className="h-9 w-28 rounded-md" />
              ))}
            </div>
          )}
          {link && (
            <span className="flex h-6 items-center">
              <SkeletonSpan className="h-3 w-24" />
            </span>
          )}
        </EmptyContent>
      )}
    </Empty>
  );
}
